import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { subscribeToNewsletter } from '../services/api';

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(2deg); }
`;

const orbit = keyframes`
  0% { transform: rotate(0deg) translateX(30px) rotate(0deg); }
  100% { transform: rotate(360deg) translateX(30px) rotate(-360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.1); opacity: 1; }
`;

const slideIn = keyframes`
  0% {
    transform: translateY(100%) scale(0.8);
    opacity: 0;
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  0% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateY(100%) scale(0.8);
    opacity: 0;
  }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0); }
  50% { opacity: 1; transform: scale(1); }
`;

// Styled Components
const FloatingContainer = styled.div<{ $visible: boolean; $isClosing: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 10000;
  animation: ${props => 
    props.$isClosing 
      ? slideOut 
      : props.$visible 
        ? slideIn 
        : 'none'
  } 0.4s ease-out forwards;
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};

  @media (max-width: 768px) {
    bottom: 10px;
    right: 10px;
    left: 10px;
    right: 10px;
  }
`;

const SpaceshipContainer = styled.div`
  position: relative;
  background: linear-gradient(135deg, 
    rgba(15, 23, 42, 0.95) 0%,
    rgba(30, 41, 59, 0.95) 50%,
    rgba(15, 23, 42, 0.95) 100%
  );
  border-radius: 20px;
  padding: 24px;
  width: 380px;
  backdrop-filter: blur(15px);
  border: 2px solid;
  border-image: linear-gradient(45deg, #667eea, #764ba2, #f093fb) 1;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.4),
    0 0 100px rgba(102, 126, 234, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  animation: ${float} 4s ease-in-out infinite;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    padding: 20px;
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle at center,
      rgba(102, 126, 234, 0.1) 0%,
      transparent 70%
    );
    animation: ${orbit} 20s linear infinite;
    pointer-events: none;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #fca5a5;
  font-size: 14px;
  z-index: 1;

  &:hover {
    background: rgba(239, 68, 68, 0.3);
    border-color: rgba(239, 68, 68, 0.6);
    transform: scale(1.1);
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
`;

const RocketIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 8px;
  animation: ${pulse} 2s ease-in-out infinite;
  display: inline-block;
`;

const Title = styled.h3`
  color: #ffffff;
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0 0 6px 0;
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: #cbd5e1;
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.9;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  z-index: 1;
`;

const InputContainer = styled.div`
  position: relative;
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 14px 16px;
  background: rgba(15, 23, 42, 0.8);
  border: 2px solid rgba(100, 116, 139, 0.3);
  border-radius: 12px;
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.3);
    background: rgba(15, 23, 42, 0.9);
  }

  &:valid {
    border-color: #10b981;
  }
`;

const SubmitButton = styled.button<{ $isLoading: boolean }>`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  padding: 14px 24px;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.$isLoading ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  opacity: ${props => props.$isLoading ? 0.7 : 1};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const FloatingStars = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
`;

const Star = styled.div<{ $delay: number; $size: number; $left: number; $top: number }>`
  position: absolute;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  background: #ffffff;
  border-radius: 50%;
  left: ${props => props.$left}%;
  top: ${props => props.$top}%;
  animation: ${sparkle} 3s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
`;

const SuccessMessage = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    rgba(16, 185, 129, 0.95) 0%,
    rgba(5, 150, 105, 0.95) 100%
  );
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  transform: ${props => props.$visible ? 'scale(1)' : 'scale(0)'};
  opacity: ${props => props.$visible ? 1 : 0};
  transition: all 0.4s ease;
  z-index: 2;
  
  h4 {
    font-size: 1.2rem;
    margin: 0 0 8px 0;
  }
  
  p {
    font-size: 0.9rem;
    margin: 0;
    opacity: 0.9;
    text-align: center;
  }
`;

// Main Component
interface FloatingNewsletterCaptureProps {
  onSubmit?: (email: string) => Promise<void>;
}

const FloatingNewsletterCapture: React.FC<FloatingNewsletterCaptureProps> = ({ onSubmit }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  // Handle scroll to show/hide component
  useEffect(() => {
    const handleScroll = () => {
      if (hasShown) return;

      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show when user has scrolled 70% of the page
      const scrollPercentage = (scrollPosition + windowHeight) / documentHeight;
      
      if (scrollPercentage > 0.7) {
        setIsVisible(true);
        setHasShown(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasShown]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    
    try {
      if (onSubmit) {
        await onSubmit(email);
      } else {
        // Default newsletter submission using the API
        await subscribeToNewsletter(email);
      }
      
      setIsSuccess(true);
      
      // Auto-close after success
      setTimeout(() => {
        handleClose();
      }, 3000);
      
    } catch (error) {
      console.error('Newsletter signup failed:', error);
      // You could add error state handling here if needed
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle close animation
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 400);
  };

  // Generate random stars
  const stars = Array.from({ length: 12 }, (_, i) => ({
    delay: i * 0.5,
    size: Math.random() * 3 + 1,
    left: Math.random() * 100,
    top: Math.random() * 100,
  }));

  if (!isVisible) return null;

  return (
    <FloatingContainer $visible={isVisible} $isClosing={isClosing}>
      <SpaceshipContainer>
        <CloseButton onClick={handleClose} aria-label="Close newsletter signup">
          ✕
        </CloseButton>
        
        <FloatingStars>
          {stars.map((star, index) => (
            <Star
              key={index}
              $delay={star.delay}
              $size={star.size}
              $left={star.left}
              $top={star.top}
            />
          ))}
        </FloatingStars>

        <Header>
          <RocketIcon>🚀</RocketIcon>
          <Title>Join the Space Explorers!</Title>
          <Subtitle>Get exclusive satellite tracking tips & launch alerts</Subtitle>
        </Header>

        <Form onSubmit={handleSubmit}>
          <InputContainer>
            <EmailInput
              type="email"
              placeholder="Enter your email for space updates..."
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting || isSuccess}
            />
          </InputContainer>
          
          <SubmitButton type="submit" $isLoading={isSubmitting} disabled={isSubmitting || isSuccess}>
            {isSubmitting ? <LoadingSpinner /> : '🛰️ Launch My Subscription'}
          </SubmitButton>
        </Form>

        <SuccessMessage $visible={isSuccess}>
          <h4>🌟 Welcome Aboard, Space Explorer!</h4>
          <p>You're now part of our cosmic community. Watch your inbox for amazing space content!</p>
        </SuccessMessage>
      </SpaceshipContainer>
    </FloatingContainer>
  );
};

export default FloatingNewsletterCapture;